import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { canonicalMessage } from "@/lib/crypto";
import { unlockAudio } from "@/lib/sound";
import { useAuth } from "@/context/AuthContext";
import BiometricModal from "@/components/BiometricModal";
import VerifyGate from "@/components/VerifyGate";
import AssistantChat from "@/components/AssistantChat";
import { ArrowLeft, ScrollText, CalendarClock, Sparkles } from "lucide-react";

function uuid() {
  return crypto.randomUUID
    ? crypto.randomUUID()
    : "dap-" + Date.now() + "-" + Math.random().toString(16).slice(2);
}

export default function CreateDap() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ counterparty_email: "", title: "", terms: "", deadline: "" });
  const [showBio, setShowBio] = useState(false);
  const [dapId] = useState(uuid());
  const [suggestions, setSuggestions] = useState([]);
  const [lastSuggestion, setLastSuggestion] = useState("");
  const [showVerify, setShowVerify] = useState(false);
  const debounce = useRef(null);
  const tier = user?.identity_verification?.verification_tier || "unverified";

  const emailVal = form.counterparty_email.trim().toLowerCase();
  const hasEmail = emailVal.includes("@");
  const counterpartyRef = emailVal;

  const valid =
    hasEmail &&
    form.title.trim().length > 2 &&
    form.terms.trim().length > 4 &&
    emailVal !== user.email.toLowerCase();

  const message = canonicalMessage({
    dap_id: dapId,
    title: form.title,
    terms: form.terms,
    creator_email: user.email,
    counterparty_ref: counterpartyRef,
  });

  const onSealed = async (signature) => {
    try {
      await api.post("/daps", {
        dap_id: dapId,
        counterparty_email: emailVal,
        title: form.title.trim(),
        terms: form.terms.trim(),
        deadline: form.deadline ? new Date(form.deadline).toISOString() : null,
        signature,
      });
      toast.success("Your side of the dap is sealed. Awaiting the other party.");
      navigate(`/dap/${dapId}`);
    } catch (e) {
      setShowBio(false);
      toast.error(e?.response?.data?.detail || "Could not create dap");
    }
  };

  const onEmailChange = (e) => {
    const v = e.target.value;
    setForm((f) => ({ ...f, counterparty_email: v }));
    clearTimeout(debounce.current);
    if (v.trim().length < 2) return setSuggestions([]);
    debounce.current = setTimeout(async () => {
      try {
        const r = await api.get(`/users/search?q=${encodeURIComponent(v.trim())}`);
        setSuggestions(r.data);
      } catch (e) {
        console.error("Counterparty search failed:", e);
      }
    }, 250);
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div data-testid="create-dap-page" className="max-w-2xl mx-auto">
      <button
        onClick={() => navigate(-1)}
        data-testid="back-btn"
        className="flex items-center gap-2 text-[#5C5A54] hover:text-[#1A1A1A] mb-8"
      >
        <ArrowLeft size={16} /> Back
      </button>

      <div className="flex items-center gap-2 text-[#E07A5F]">
        <ScrollText size={18} />
        <p className="overline">New Handshake</p>
      </div>
      <h1 className="font-serif-dl text-4xl sm:text-5xl tracking-tight mt-2">Draft a Dap</h1>
      <p className="text-[#5C5A54] mt-3 leading-relaxed">
        Set down what you're agreeing to. When you seal it with your biometric,
        it's sent to the other party to seal in kind. Only then is it written to
        the ledger.
      </p>

      <div className="mt-10 bg-white border border-[#DEDBD3] rounded-lg p-6 sm:p-8 space-y-6">
        <div className="relative">
          <label className="block text-sm font-bold text-[#5C5A54] uppercase tracking-wider mb-2">
            Counterparty email
          </label>
          <input
            data-testid="counterparty-input"
            type="email"
            value={form.counterparty_email}
            onChange={onEmailChange}
            autoComplete="off"
            placeholder="who are you giving your word to?"
            className="bg-white border border-[#DEDBD3] rounded-md px-4 py-3 w-full focus:outline-none focus:ring-1 focus:ring-[#1B4D3E] focus:border-[#1B4D3E] transition-all"
          />
          {suggestions.length > 0 && (
            <div className="absolute z-10 mt-1 w-full bg-white border border-[#DEDBD3] rounded-md shadow-lg overflow-hidden">
              {suggestions.map((s) => (
                <button
                  key={s.email}
                  type="button"
                  data-testid={`suggestion-${s.email}`}
                  onClick={() => {
                    setForm((f) => ({ ...f, counterparty_email: s.email }));
                    setSuggestions([]);
                  }}
                  className="w-full text-left px-4 py-2 hover:bg-[#F9F8F6] flex items-center gap-2"
                >
                  <span className="text-sm font-medium">{s.name}</span>
                  <span className="text-xs text-[#8A877F]">{s.email}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div>
          <label className="block text-sm font-bold text-[#5C5A54] uppercase tracking-wider mb-2">
            The agreement
          </label>
          <input
            data-testid="title-input"
            value={form.title}
            onChange={set("title")}
            placeholder="e.g. I'll cover rent this month"
            className="bg-white border border-[#DEDBD3] rounded-md px-4 py-3 w-full focus:outline-none focus:ring-1 focus:ring-[#1B4D3E] focus:border-[#1B4D3E] transition-all"
          />
        </div>
        <div>
          <label className="block text-sm font-bold text-[#5C5A54] uppercase tracking-wider mb-2">
            Terms & understanding
          </label>
          <textarea
            data-testid="terms-input"
            value={form.terms}
            onChange={set("terms")}
            rows={5}
            placeholder="Spell out the understanding in plain words…"
            className="bg-white border border-[#DEDBD3] rounded-md px-4 py-3 w-full focus:outline-none focus:ring-1 focus:ring-[#1B4D3E] focus:border-[#1B4D3E] transition-all resize-none"
          />
        </div>
        <div>
          <label className="text-sm font-bold text-[#5C5A54] uppercase tracking-wider mb-2 flex items-center gap-2">
            <CalendarClock size={15} /> Deadline <span className="text-[#A3A095] normal-case tracking-normal font-normal">(optional — date &amp; time)</span>
          </label>
          <input
            data-testid="deadline-input"
            type="datetime-local"
            value={form.deadline}
            onChange={set("deadline")}
            className="bg-white border border-[#DEDBD3] rounded-md px-4 py-3 w-full focus:outline-none focus:ring-1 focus:ring-[#1B4D3E] focus:border-[#1B4D3E] transition-all"
          />
        </div>

        <button
          data-testid="seal-dap-btn"
          disabled={!valid}
          onClick={() => {
            unlockAudio();
            tier === "unverified" ? setShowVerify(true) : setShowBio(true);
          }}
          className="w-full py-4 bg-[#E07A5F] hover:bg-[#C96A51] disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-medium tracking-wide transition-colors"
        >
          Seal with Biometrics
        </button>
      </div>

      <div className="mt-6 bg-white border border-[#DEDBD3] rounded-lg p-6 sm:p-8" data-testid="advisor-panel">
        <div className="flex items-center gap-2 text-[#B5541F]">
          <Sparkles size={18} />
          <p className="overline">Dap Advisor</p>
        </div>
        <p className="text-sm text-[#5C5A54] mt-2 mb-4">
          Not sure how to word it fairly? Ask the advisor to review your draft or
          suggest clear terms both sides can honor.
        </p>
        <AssistantChat
          mode="advisor"
          sessionId={`advisor-${dapId}`}
          welcome="Share what you're agreeing to, or tap ‘Review my draft’ and I'll help you make the terms clear and fair."
          placeholder="e.g. How do I phrase a fair repayment agreement?"
          heightClass="h-64"
          onLatestReply={setLastSuggestion}
          quickActions={[
            {
              label: "Review my draft",
              message: () =>
                `Please review this handshake agreement draft and suggest clearer, fairer wording. Point out anything ambiguous or missing.\n\nTitle: ${
                  form.title || "(none)"
                }\nTerms: ${form.terms || "(none)"}${
                  form.deadline ? `\nDeadline: ${form.deadline}` : ""
                }`,
            },
            {
              label: "Write ready-to-paste terms",
              message: () =>
                `Write the final terms for a handshake agreement titled "${
                  form.title || "our agreement"
                }". Output ONLY the terms text I can paste directly — no preamble, no headings, no commentary. Keep it honor-based, clear and specific.`,
            },
          ]}
        />
        {lastSuggestion && (
          <button
            type="button"
            data-testid="insert-terms-btn"
            onClick={() => {
              setForm((f) => ({ ...f, terms: lastSuggestion }));
              toast.success("Advisor's suggestion inserted into the terms.");
            }}
            className="mt-4 w-full py-2.5 border border-[#1B4D3E] text-[#1B4D3E] rounded-lg hover:bg-[#F0EBE1] flex items-center justify-center gap-2 text-sm font-medium transition-colors"
          >
            <ScrollText size={15} /> Insert the Advisor's latest reply into Terms
          </button>
        )}
      </div>

      <BiometricModal
        open={showBio}
        onClose={() => setShowBio(false)}
        message={message}
        actionLabel="Seal my word"
        onComplete={onSealed}
      />
      <VerifyGate open={showVerify} onClose={() => setShowVerify(false)} />
    </div>
  );
}
